// My Trading Journey's frame (JourneyHub: switch, period, headline cards, the Log window) and adding
// a milestone. The three parts are JourneyMoney.js, JourneyBetter.js and JourneyGrowth.js.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { JourneyCtx, JourneyLog, storyOf } from './JourneyStory';
import JourneyShare from './JourneyShare';
import { api } from './api';
import { JOURNEY_PERIODS, careerEdge, journeyHeadlines, journeyOverview, BUILT_IN_MILESTONES, MILESTONE_TEXT, MILESTONE_TYPES, SUGGESTED_MILESTONES, scopeJourney } from './journey';
import { tradingDayKey, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const wholeUsd = (n) => usd.format(Math.round(n)).replace(/\.00$/, '');
const dayText = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', day: 'numeric', year: 'numeric' });

// The switch at the top of the Journey: prop firms, personal accounts, or everything together.
const SCOPES = [
  ['prop', 'Prop firms', 'Payouts minus what you paid the firms'],
  ['personal', 'Personal', 'Your own money: trading profit minus costs'],
  ['all', 'All together', 'Everything combined'],
];
function ScopeSwitch({ scope, onScope }) {
  const { t } = useI18n();
  return (
    <div className="jh-scope" role="tablist" aria-label={t('Which accounts')}>
      {SCOPES.map(([id, label, hint]) => (
        <button key={id} type="button" role="tab" aria-selected={scope === id} className={`jh-scope-btn${scope === id ? ' on' : ''}`} onClick={() => onScope(id)}>
          <strong>{t(label)}</strong>
          <span>{t(hint)}</span>
        </button>
      ))}
    </div>
  );
}

// The new Journey's period selector: all time, the last 3 or 12 months, or a year with trades.
const PERIOD_KEY = 'mm-journey-period';
const PERIOD_NAMES = { all: 'All time', last3: 'Last 3 months', last12: 'Last 12 months' };
function readPeriod() {
  try {
    return localStorage.getItem(PERIOD_KEY) || 'all';
  } catch {
    return 'all';
  }
}
function PeriodPicker({ period, onPeriod, years }) {
  const { t } = useI18n();
  return (
    <div className="insights-tabs jh-periods" role="group" aria-label={t('Period')}>
      {[...JOURNEY_PERIODS, ...years].map((p) => (
        <button key={p} type="button" className={`secondary small${period === p ? ' active' : ''}`} aria-pressed={period === p} onClick={() => onPeriod(p)}>
          {PERIOD_NAMES[p] ? t(PERIOD_NAMES[p]) : p}
        </button>
      ))}
    </div>
  );
}

const signedWhole = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${wholeUsd(Math.abs(n))}`;

// One card per part at the top of the new Journey, each with its headline number and a link in.
function Headlines({ accounts, trades, scope, period, edge }) {
  const { t } = useI18n();
  const [expenses, setExpenses] = useState(null);
  useEffect(() => { api('/expenses').then(setExpenses).catch(() => setExpenses([])); }, []);
  const h = useMemo(() => {
    if (!expenses) return null;
    const shown = scopeJourney(scope, accounts, trades, expenses);
    const o = journeyOverview(shown.accounts, trades, shown.expenses);
    return journeyHeadlines(o, edge ? careerEdge(edge.trades, { library: edge.library, plays: edge.plays }) : null, period);
  }, [expenses, accounts, trades, scope, period, edge && edge.trades, edge && edge.library, edge && edge.plays]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!h) return null;
  const b = h.better;
  const cards = [
    {
      to: '/app/journey/profitable', label: t('Am I profitable?'),
      value: signedWhole(h.money.net), tone: tone(h.money.net),
      note: t('real money · last 3 months {amount}', { amount: signedWhole(h.money.last3) }),
    },
    {
      to: '/app/journey/better', label: t('Am I getting better?'),
      value: !b ? '—' : b.kind === 'lifespan' ? t('{n} days', { n: Math.round(b.recent) }) : signedWhole(b.recent),
      tone: b ? (b.recent > b.early ? 'gain' : b.recent < b.early ? 'loss' : '') : '',
      note: !b ? t('Needs a few more months of trading')
        : b.kind === 'lifespan' ? t(b.recent >= b.early ? 'blown accounts now last this long, up from {n} days' : 'blown accounts now last this long, down from {n} days', { n: Math.round(b.early) })
          : t('your average month lately, against {amount} in your first months', { amount: signedWhole(b.early) }),
    },
    {
      to: '/app/journey/edge-milestones', label: t('My edge & milestones'),
      value: h.setup ? h.setup.label : '—', tone: '',
      note: h.setup ? t('{amount} a trade over {n} trades', { amount: signedWhole(h.setup.expectancy), n: h.setup.count }) : t('No setup has proven itself over the long run yet.'),
    },
  ];
  return (
    <div className="jh-headlines">
      {cards.map((c) => (
        <Link key={c.to} to={c.to} className="jh-headline">
          <span className="jh-headline-label">{c.label}</span>
          <strong className={c.tone}>{c.value}</strong>
          <span className="jh-headline-note">{c.note}</span>
        </Link>
      ))}
    </div>
  );
}

// The Journey's own page around whichever part is open: the switch, the header, the period
// selector, the three headline cards and the Log window every part can open. After something is
// logged the open part loads again with it.
export function JourneyHub({ trades, accounts = [], allTrades = trades, notes, user, scope = 'all', onScope, onUserChange, onChanged, onSessionExpired, edge = null, children }) {
  const { t } = useI18n();
  const [period, setPeriodState] = useState(readPeriod);
  const setPeriod = (p) => {
    setPeriodState(p);
    try {
      localStorage.setItem(PERIOD_KEY, p);
    } catch {
      // Private mode: the choice lasts until the page reloads.
    }
  };
  const years = useMemo(() => [...new Set(allTrades.map((x) => tradingDayKey(x.date).slice(0, 4)))].sort().reverse(), [allTrades]);
  const shownPeriod = JOURNEY_PERIODS.includes(period) || years.includes(period) ? period : 'all';
  const first = useMemo(() => trades.map((x) => x.date).sort()[0], [trades]);
  const [log, setLog] = useState(null);
  const [version, setVersion] = useState(0);
  const [sharing, setSharing] = useState(false);
  const story = useMemo(() => storyOf(user), [user]);
  async function saveStory(next) {
    if (!onUserChange || !user) return;
    onUserChange({ ...user, journeyStory: next });
    const saved = await api('/journey/story', { method: 'PUT', body: next });
    onUserChange({ ...user, journeyStory: saved });
  }
  const ctx = {
    openLog: onUserChange ? (mode, preset) => setLog({ mode: mode || 'expense', preset: preset || {} }) : null,
    story, saveStory: onUserChange ? saveStory : null,
    scope, setScope: onScope || null, allAccounts: accounts, allTrades, period: shownPeriod,
    scopeExpenses: (list) => scopeJourney(scope, accounts, [], list).expenses,
  };
  async function saved(kind) {
    setLog(null);
    if (kind === 'moment') return;
    if (onChanged) await onChanged();
    setVersion((v) => v + 1);
  }
  return (
    <JourneyCtx.Provider value={ctx}>
      <div className="journey-hub">
        {onScope && <ScopeSwitch scope={scope} onScope={onScope} />}
        <header className="jh-head">
          <div>
            <h1 className="routine-title">{t('My Trading Journey')}</h1>
            <p className="subtitle">
              {first ? t('Every dollar in, every dollar out, every account, since your first trade on {day}.', { day: dayText(first.slice(0, 10)) })
                : t('Every dollar in, every dollar out, every account.')}
            </p>
          </div>
          {ctx.openLog && (
            <div className="jh-actions">
              {trades.length > 0 && <button type="button" className="secondary" onClick={() => setSharing(true)}>{t('Share my journey')}</button>}
              <button type="button" onClick={() => ctx.openLog('expense')}>+ {t('Log to your journey')}</button>
            </div>
          )}
        </header>
        <PeriodPicker period={shownPeriod} onPeriod={setPeriod} years={years} />
        <Headlines accounts={accounts} trades={trades} scope={scope} period={shownPeriod} edge={edge} />
        <div className="jh-body" key={version}>{children}</div>
        {log && (
          <JourneyLog mode={log.mode} preset={log.preset} accounts={accounts} story={story} saveStory={saveStory}
            onSaved={saved} onClose={() => setLog(null)} onSessionExpired={onSessionExpired} />
        )}
        {sharing && <JourneyShare accounts={accounts} trades={trades} notes={notes} user={user} onClose={() => setSharing(false)} />}
      </div>
    </JourneyCtx.Provider>
  );
}

// --- Then vs now --------------------------------------------------------------------------------

// --- Monthly recap ------------------------------------------------------------------------------

// --- Milestones ---------------------------------------------------------------------------------

// Adding a milestone: one of the suggestions, or their own (type, target, name).
export function AddMilestone({ settings, onSave, onClose }) {
  const { t } = useI18n();
  const added = new Set(settings.added || []);
  const hidden = new Set(settings.hidden || []);
  const [form, setForm] = useState({ type: 'trades', target: '', title: '' });
  const type = MILESTONE_TYPES[form.type];
  const restorable = BUILT_IN_MILESTONES.filter((m) => hidden.has(m.id));
  function addOwn(e) {
    e.preventDefault();
    const target = type.unit === 'none' ? 1 : Number(form.target);
    if (!(target > 0)) return;
    const id = `c-${Date.now().toString(36)}`;
    onSave({ ...settings, custom: [...(settings.custom || []), { id, type: form.type, target, title: form.title.trim() }] });
    setForm({ type: form.type, target: '', title: '' });
  }
  const card = (m, action) => {
    const [icon, name, about] = MILESTONE_TEXT[m.id];
    return (
      <li key={m.id} className="jm-suggest">
        <span className="jm-icon" aria-hidden="true">{icon}</span>
        <span><strong>{t(name)}</strong><span className="field-hint">{t(about)}</span></span>
        <button type="button" className="secondary small" onClick={action}>+ {t('Add')}</button>
      </li>
    );
  };
  return (
    <section className="jm-add">
      <div className="jo-card-head">
        <h2>{t('Add a milestone')}</h2>
        <button type="button" className="icon-button" aria-label={t('Close')} onClick={onClose}>&times;</button>
      </div>
      <form className="jm-own" onSubmit={addOwn}>
        <h3>{t('Make your own')}</h3>
        <div className="fields">
          <label>{t('What to count')}
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {Object.entries(MILESTONE_TYPES).map(([id, x]) => <option key={id} value={id}>{t(x.label)}</option>)}
            </select>
          </label>
          {type.unit !== 'none' && (
            <label>{type.unit === '$' ? t('Target (dollars)') : type.unit === 'days' ? t('Target (days)') : t('Target (how many)')}
              <input type="number" min="1" step={type.unit === '$' ? 'any' : '1'} value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} required />
            </label>
          )}
          <label>{t('Name (optional)')}
            <input value={form.title} maxLength={60} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('e.g. My first $1,000 month')} />
          </label>
        </div>
        <button type="submit" className="small">{t('Add milestone')}</button>
      </form>
      <h3>{t('Suggestions')}</h3>
      <ul className="jm-suggest-list">
        {SUGGESTED_MILESTONES.filter((m) => !added.has(m.id)).map((m) => card(m, () => onSave({ ...settings, added: [...(settings.added || []), m.id] })))}
      </ul>
      {restorable.length > 0 && (
        <>
          <h3>{t('Ones you hid')}</h3>
          <ul className="jm-suggest-list">
            {restorable.map((m) => card(m, () => onSave({ ...settings, hidden: (settings.hidden || []).filter((x) => x !== m.id) })))}
          </ul>
        </>
      )}
    </section>
  );
}
